/**
 * Error codes for all failure paths.
 * Format: COMPONENT_OPERATION_REASON
 * When something fails, log the code. User sends code to developer, developer knows exactly what broke.
 */

export const ErrorCodes = {
  // ── LOAD Interceptor ──────────────────────────────────────────────────────
  LOAD_DATA_TOKEN_MISSING: "LOAD_DATA_TOKEN_MISSING",
  LOAD_DATA_URL_MISSING: "LOAD_DATA_URL_MISSING",
  LOAD_DATA_ITEM_ID_MISSING: "LOAD_DATA_ITEM_ID_MISSING",
  LOAD_CANVAS_NOT_FOUND: "LOAD_CANVAS_NOT_FOUND",
  LOAD_VIDEO_NOT_FOUND: "LOAD_VIDEO_NOT_FOUND",

  // ── Progress Reporter ─────────────────────────────────────────────────────
  PROG_START_EXCEPTION: "PROG_START_EXCEPTION",
  PROG_START_NETWORK_ERROR: "PROG_START_NETWORK_ERROR",
  PROG_START_AUTH_FAILED: "PROG_START_AUTH_FAILED",
  PROG_START_HTTP_ERROR: "PROG_START_HTTP_ERROR",

  PROG_REPORT_EXCEPTION: "PROG_REPORT_EXCEPTION",
  PROG_REPORT_NETWORK_ERROR: "PROG_REPORT_NETWORK_ERROR",
  PROG_REPORT_AUTH_FAILED: "PROG_REPORT_AUTH_FAILED",
  PROG_REPORT_HTTP_ERROR: "PROG_REPORT_HTTP_ERROR",

  PROG_STOP_EXCEPTION: "PROG_STOP_EXCEPTION",
  PROG_STOP_NETWORK_ERROR: "PROG_STOP_NETWORK_ERROR",
  PROG_STOP_AUTH_FAILED: "PROG_STOP_AUTH_FAILED",
  PROG_STOP_HTTP_ERROR: "PROG_STOP_HTTP_ERROR",

  // ── Subtitle Manager ──────────────────────────────────────────────────────
  SUB_LOAD_VIDEO_ELEMENT_MISSING: "SUB_LOAD_VIDEO_ELEMENT_MISSING",
  SUB_LOAD_CODEC_UNSUPPORTED: "SUB_LOAD_CODEC_UNSUPPORTED",
  SUB_LOAD_URL_MISSING: "SUB_LOAD_URL_MISSING",
  SUB_LOAD_FETCH_404: "SUB_LOAD_FETCH_404",
  SUB_LOAD_FETCH_403: "SUB_LOAD_FETCH_403",
  SUB_LOAD_FETCH_TIMEOUT: "SUB_LOAD_FETCH_TIMEOUT",
  SUB_LOAD_FETCH_NETWORK_ERROR: "SUB_LOAD_FETCH_NETWORK_ERROR",
  SUB_LOAD_PARSE_ERROR: "SUB_LOAD_PARSE_ERROR",
  SUB_LOAD_RENDER_ERROR: "SUB_LOAD_RENDER_ERROR",
  SUB_LOAD_EXCEPTION: "SUB_LOAD_EXCEPTION",

  // ── Jellyfin Client ──────────────────────────────────────────────────────
  JELLY_AUTH_401: "JELLY_AUTH_401",
  JELLY_AUTH_403: "JELLY_AUTH_403",
  JELLY_SERVER_500: "JELLY_SERVER_500",
  JELLY_SERVER_UNAVAILABLE: "JELLY_SERVER_UNAVAILABLE",
  JELLY_NETWORK_TIMEOUT: "JELLY_NETWORK_TIMEOUT",
  JELLY_NETWORK_ERROR: "JELLY_NETWORK_ERROR",
  JELLY_INVALID_URL: "JELLY_INVALID_URL",

  // ── CAF Playback Errors ───────────────────────────────────────────────────
  CAF_ERROR_UNKNOWN_CODE: "CAF_ERROR_UNKNOWN_CODE",
  CAF_ERROR_MEDIA_ABORTED: "CAF_ERROR_MEDIA_ABORTED",
  CAF_ERROR_MEDIA_NETWORK: "CAF_ERROR_MEDIA_NETWORK",
  CAF_ERROR_MEDIA_DECODE: "CAF_ERROR_MEDIA_DECODE",
  CAF_ERROR_MEDIA_NOT_SUPPORTED: "CAF_ERROR_MEDIA_NOT_SUPPORTED",
  CAF_ERROR_MEDIA_SRC_NOT_SUPPORTED: "CAF_ERROR_MEDIA_SRC_NOT_SUPPORTED",

  // ── State Machine ─────────────────────────────────────────────────────────
  STATE_INVALID_TRANSITION: "STATE_INVALID_TRANSITION",
  STATE_CONTEXT_MISSING: "STATE_CONTEXT_MISSING",
  STATE_SESSION_MISSING: "STATE_SESSION_MISSING",
  STATE_REPORTER_MISSING: "STATE_REPORTER_MISSING",

  // ── Jellyfin API Issues ──────────────────────────────────────────────────
  API_RESPONSE_400: "API_RESPONSE_400",
  API_RESPONSE_401: "API_RESPONSE_401",
  API_RESPONSE_403: "API_RESPONSE_403",
  API_RESPONSE_404: "API_RESPONSE_404",
  API_RESPONSE_500: "API_RESPONSE_500",
  API_RESPONSE_503: "API_RESPONSE_503",
  API_MALFORMED_RESPONSE: "API_MALFORMED_RESPONSE",
} as const

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes]

/**
 * Google Cast `detailedErrorCode` → symbolic name.  These are CAF / Shaka's
 * numeric codes (https://developers.google.com/cast/docs/web_receiver/error_codes),
 * orthogonal to our ErrorCodes table above.
 *
 * Added 2026-04-19: during investigation of a post-248s-pause skip-to-next-
 * episode incident we only had the numeric code "102" in logs and had to
 * look it up by hand — turns out MEDIA_DECODE, not the transport/expiry code
 * we were assuming.  Keeping this table here so future ERROR events log
 * `102 (MEDIA_DECODE)` inline and we can triage without guessing.  If the
 * skip bug recurs the log line for the triggering error now carries enough
 * info to tell transcoding-session-expired (3xx HLS_NETWORK_*) from
 * decoder-level (1xx MEDIA_*) from manifest-level (4xx *_MANIFEST_*).
 */
export const CafDetailedErrorNames: Readonly<Record<number, string>> = {
  100: "MEDIA_UNKNOWN",
  101: "MEDIA_ABORTED",
  102: "MEDIA_DECODE",
  103: "MEDIA_NETWORK",
  104: "MEDIA_SRC_NOT_SUPPORTED",
  110: "SOURCE_BUFFER_FAILURE",
  201: "MEDIAKEYS_UNKNOWN",
  202: "MEDIAKEYS_NETWORK",
  203: "MEDIAKEYS_UNSUPPORTED",
  204: "MEDIAKEYS_WEBCRYPTO",
  301: "NETWORK_UNKNOWN",
  311: "HLS_NETWORK_MASTER_PLAYLIST",
  312: "HLS_NETWORK_PLAYLIST",
  313: "HLS_NETWORK_NO_KEY_RESPONSE",
  314: "HLS_NETWORK_KEY_LOAD",
  315: "HLS_NETWORK_INVALID_SEGMENT",
  316: "HLS_SEGMENT_PARSING",
  321: "DASH_NETWORK",
  322: "DASH_NO_INIT",
  331: "SMOOTH_NETWORK",
  332: "SMOOTH_NO_MEDIA_DATA",
  401: "MANIFEST_UNKNOWN",
  411: "HLS_MANIFEST_MASTER",
  412: "HLS_MANIFEST_PLAYLIST",
  421: "DASH_MANIFEST_UNKNOWN",
  422: "DASH_MANIFEST_NO_PERIODS",
  423: "DASH_MANIFEST_NO_MIMETYPE",
  424: "DASH_INVALID_SEGMENT_INFO",
  431: "SMOOTH_MANIFEST",
  501: "SEGMENT_UNKNOWN",
  502: "SEGMENT_NETWORK",
  503: "HLS_SEGMENT_PARSING",
  601: "TEXT_UNKNOWN",
  900: "APP",
  901: "BREAK_CLIP_LOADING_ERROR",
  902: "BREAK_SEEK_INTERCEPTOR_ERROR",
  903: "IMAGE_ERROR",
  904: "LOAD_INTERRUPTED",
  905: "LOAD_FAILED",
  906: "MEDIA_ERROR_MESSAGE",
  999: "GENERIC_LOAD_ERROR",
}

export function formatCafDetailedError(code: string | number | undefined): string {
  if (code === undefined || code === null) return "UNKNOWN"
  const n = typeof code === "number" ? code : Number(code)
  if (!Number.isFinite(n)) return String(code)
  const name = CafDetailedErrorNames[n]
  return name ? `${n} (${name})` : `${n} (UNMAPPED)`
}

/**
 * Error messages mapped to codes.
 * When logging an error, include both code and message.
 */
export const ErrorMessages: Record<ErrorCode, string> = {
  LOAD_DATA_TOKEN_MISSING: "LOAD customData missing Jellyfin token",
  LOAD_DATA_URL_MISSING: "LOAD customData missing Jellyfin server URL",
  LOAD_DATA_ITEM_ID_MISSING: "LOAD customData missing item ID",
  LOAD_CANVAS_NOT_FOUND: "Subtitle canvas element not found in DOM",
  LOAD_VIDEO_NOT_FOUND: "Video element not found (CAF shadow DOM unreachable)",

  PROG_START_EXCEPTION: "Progress reporter start() threw exception",
  PROG_START_NETWORK_ERROR: "Progress start: network unreachable",
  PROG_START_AUTH_FAILED: "Progress start: auth token invalid (401)",
  PROG_START_HTTP_ERROR: "Progress start: HTTP error from server",

  PROG_REPORT_EXCEPTION: "Progress report threw exception",
  PROG_REPORT_NETWORK_ERROR: "Progress report: network unreachable",
  PROG_REPORT_AUTH_FAILED: "Progress report: auth token invalid (401)",
  PROG_REPORT_HTTP_ERROR: "Progress report: HTTP error from server",

  PROG_STOP_EXCEPTION: "Progress stop() threw exception",
  PROG_STOP_NETWORK_ERROR: "Progress stop: network unreachable",
  PROG_STOP_AUTH_FAILED: "Progress stop: auth token invalid (401)",
  PROG_STOP_HTTP_ERROR: "Progress stop: HTTP error from server",

  SUB_LOAD_VIDEO_ELEMENT_MISSING: "Subtitle load: video element not found",
  SUB_LOAD_CODEC_UNSUPPORTED: "Subtitle codec not supported (not ass/pgs/srt/vtt)",
  SUB_LOAD_URL_MISSING: "Subtitle URL missing from load data",
  SUB_LOAD_FETCH_404: "Subtitle URL returned 404 (file not found)",
  SUB_LOAD_FETCH_403: "Subtitle URL returned 403 (forbidden)",
  SUB_LOAD_FETCH_TIMEOUT: "Subtitle fetch timed out",
  SUB_LOAD_FETCH_NETWORK_ERROR: "Subtitle fetch: network error",
  SUB_LOAD_PARSE_ERROR: "Subtitle parsing failed",
  SUB_LOAD_RENDER_ERROR: "Subtitle rendering failed",
  SUB_LOAD_EXCEPTION: "Subtitle load threw exception",

  JELLY_AUTH_401: "Jellyfin auth failed (401 Unauthorized)",
  JELLY_AUTH_403: "Jellyfin auth failed (403 Forbidden)",
  JELLY_SERVER_500: "Jellyfin server error (500)",
  JELLY_SERVER_UNAVAILABLE: "Jellyfin server unavailable (503)",
  JELLY_NETWORK_TIMEOUT: "Jellyfin request timed out",
  JELLY_NETWORK_ERROR: "Jellyfin network error",
  JELLY_INVALID_URL: "Invalid Jellyfin URL",

  CAF_ERROR_UNKNOWN_CODE: "CAF playback error (code unknown)",
  CAF_ERROR_MEDIA_ABORTED: "Media playback aborted",
  CAF_ERROR_MEDIA_NETWORK: "Media network error",
  CAF_ERROR_MEDIA_DECODE: "Media decode error (unsupported codec?)",
  CAF_ERROR_MEDIA_NOT_SUPPORTED: "Media format not supported",
  CAF_ERROR_MEDIA_SRC_NOT_SUPPORTED: "Media source URL not accessible",

  STATE_INVALID_TRANSITION: "State machine invalid transition",
  STATE_CONTEXT_MISSING: "State machine context is null",
  STATE_SESSION_MISSING: "State machine: active session missing",
  STATE_REPORTER_MISSING: "State machine: progress reporter missing",

  API_RESPONSE_400: "Jellyfin API 400 Bad Request",
  API_RESPONSE_401: "Jellyfin API 401 Unauthorized",
  API_RESPONSE_403: "Jellyfin API 403 Forbidden",
  API_RESPONSE_404: "Jellyfin API 404 Not Found",
  API_RESPONSE_500: "Jellyfin API 500 Server Error",
  API_RESPONSE_503: "Jellyfin API 503 Unavailable",
  API_MALFORMED_RESPONSE: "Jellyfin API returned malformed response",
}
